// LOOM-PRODUCT-COUPLED replay renderer (composes the ported loomweb views).
/**
 * Render a fetched `ReplayPayload` (a converted real loom session) inline: the
 * session title + workspace / ADE-role / model chrome, a play-bar, then each
 * message as a user or assistant bubble whose loom `MessageBlock[]` render
 * through the ported block-renderer.
 *
 * The play-bar drives progressive reveal (a "回放"): blocks are revealed up to a
 * global index; ▶ auto-advances one block at a time (restarting from the top when
 * at the end), the scrubber / ⏮ / ⏭ seek + pause. The conversation pane is
 * height-capped + scrollable so even a long session stays embeddable (and never
 * buries blocks that follow it), and reveal keeps the newest block in view.
 *
 * NARRATION (旁白配音, FEATURE_IA_REPLAY phase-c ③): when a `narration` manifest
 * is supplied, the play-bar grows a 🔊 toggle. With narration ON, playback is
 * AUDIO-DRIVEN and stays 同轨 (same-track): for each chapter we play its wav and
 * reveal that chapter's blocks spread evenly across the wav's measured duration
 * (interval = durationMs / blocksInChapter, clamped REVEAL_MIN_MS..REVEAL_MAX_MS),
 * and we ONLY advance to the next chapter when the current chapter's audio has
 * ENDED. So the reveal never outruns the audio, and the audio never talks over a
 * frozen screen. Muting / toggling narration off falls back to the silent
 * per-block reveal below.
 */
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReplayNarration, ReplayPayload } from "@shared/types";
import { BlockRenderer } from "./block-renderer";
import { LiveRunBlock, useLoomConnected } from "../../loom/LiveRunBlock";

/** Per-block auto-advance dwell while playing WITHOUT narration (ms). */
const DWELL_MS = 700;
/** Lower bound for a narration-driven per-block reveal interval (ms). */
const REVEAL_MIN_MS = 350;
/** Upper bound for a narration-driven per-block reveal interval (ms). */
const REVEAL_MAX_MS = 3500;
/** Max characters of a chapter line shown in the ToC item before ellipsis. */
const TOC_LABEL_MAX = 24;
/** Minimum chapters required before a ToC is worth rendering. */
const TOC_MIN_CHAPTERS = 2;

/**
 * Truncate a chapter line for a compact ToC item, appending an ellipsis when
 * the source exceeds `TOC_LABEL_MAX` so long narration lines stay one row.
 */
function truncateLabel(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= TOC_LABEL_MAX) return trimmed;
  return `${trimmed.slice(0, TOC_LABEL_MAX)}…`;
}

/** A small meta chip (workspace / role / model) shown in the replay header. */
function MetaChip({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <span className="replay-meta-chip">
      <span className="replay-meta-key">{label}</span>
      <span className="replay-meta-val">{value}</span>
    </span>
  );
}

/** Props for the replay view. `narration` + `sessionId` are phase-c ③ additions. */
interface ReplayViewProps {
  payload: ReplayPayload;
  /** loom session id, used to build the per-chapter audio URLs. */
  sessionId?: string;
  /** Optional narration manifest; when present, enables the 🔊 narration mode. */
  narration?: ReplayNarration;
  /**
   * Render a chapter table-of-contents (章节目录) alongside the conversation so
   * long sessions are navigable. Requires a `narration` manifest with at least
   * two chapters; defaults off (the inline chat replay leaves it hidden).
   */
  showToc?: boolean;
  /**
   * Whether loom-live (FEATURE_IA_LOOM_LIVE) is on. The「在你的织灵里试跑」run-bar
   * is offered only when this is true AND the 织灵 is connected — without it the
   * replay is view-only, even though replay (R3) itself may be on. Defaults off.
   */
  loomLiveEnabled?: boolean;
}

export function ReplayView({
  payload,
  sessionId,
  narration,
  showToc = false,
  loomLiveEnabled = false
}: ReplayViewProps): JSX.Element {
  // Try it yourself (C→D funnel, per the answer-rendering design): a connected
  // user can run THIS recorded case live in their own 织灵 from the play-bar. The
  // seed prompt is the case's first user message.
  const connected = useLoomConnected();
  const [tryMode, setTryMode] = useState(false);
  const seedPrompt = useMemo(() => {
    const firstUser = payload.messages.find((m) => m.role === "user");
    const tb = firstUser?.blocks.find((b) => b.type === "text");
    return tb !== undefined && "content" in tb ? String((tb as { content?: string }).content ?? "") : "";
  }, [payload]);

  // Total blocks across all messages — the reveal / scrub axis.
  const total = useMemo(
    () => payload.messages.reduce((n, m) => n + m.blocks.length, 0),
    [payload]
  );
  const [shown, setShown] = useState(total);
  const [playing, setPlaying] = useState(false);
  const convRef = useRef<HTMLDivElement>(null);

  // Narration state. `narrationOn` is the user's 🔊 toggle (only meaningful when a
  // manifest exists). `chapterIdx` is the chapter whose audio is currently
  // playing; `caption` is its 旁白字幕 line.
  const hasNarration = narration !== undefined && narration.chapters.length > 0;
  const [narrationOn, setNarrationOn] = useState(hasNarration);
  const [chapterIdx, setChapterIdx] = useState(0);
  const [caption, setCaption] = useState<string>("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Pending reveal timers for the active chapter (cleared on pause/scrub/advance).
  const revealTimers = useRef<number[]>([]);

  const narrationActive = hasNarration && narrationOn;

  /** Clear any scheduled per-block reveal timers for the current chapter. */
  const clearRevealTimers = useCallback((): void => {
    for (const t of revealTimers.current) window.clearTimeout(t);
    revealTimers.current = [];
  }, []);

  /** Stop audio + cancel reveal timers (used on pause / scrub / unmount). */
  const stopAudio = useCallback((): void => {
    clearRevealTimers();
    const el = audioRef.current;
    if (el !== null) {
      el.onended = null;
      el.pause();
      audioRef.current = null;
    }
  }, [clearRevealTimers]);

  // Reset when the payload changes (a different session).
  useEffect(() => {
    setShown(total);
    setPlaying(false);
    setChapterIdx(0);
    setCaption("");
  }, [total]);

  // Re-sync the toggle default if a manifest arrives after mount.
  useEffect(() => {
    setNarrationOn(hasNarration);
  }, [hasNarration]);

  /**
   * Start a narration chapter: set the caption, reveal its blocks spread evenly
   * across the wav duration (this is the 同轨 reveal pacing), and play its audio;
   * when the audio ENDS, advance to the next chapter (the 同轨 advance contract).
   */
  const startChapter = useCallback(
    (idx: number): void => {
      if (narration === undefined || sessionId === undefined) return;
      const chapter = narration.chapters[idx];
      if (chapter === undefined) {
        setPlaying(false);
        return;
      }
      clearRevealTimers();
      setChapterIdx(idx);
      setCaption(chapter.text);

      // Reveal the chapter's blocks spread across the audio duration. The screen
      // is already at fromBlock (we never rewind past it); we reveal each block up
      // to toBlock, the last landing before the audio ends.
      const count = chapter.toBlock - chapter.fromBlock + 1;
      const raw = chapter.durationMs / Math.max(1, count);
      const interval = Math.min(REVEAL_MAX_MS, Math.max(REVEAL_MIN_MS, raw));
      setShown(chapter.fromBlock); // freeze at the chapter start, then fill in
      for (let i = 0; i < count; i++) {
        const target = chapter.fromBlock + i + 1; // reveal blocks fromBlock..toBlock
        const t = window.setTimeout(() => setShown(target), Math.round(interval * (i + 1)));
        revealTimers.current.push(t);
      }

      // Audio drives chapter advance: do not move on until THIS audio has ended.
      const el = new Audio(
        `/api/replay/${encodeURIComponent(sessionId)}/audio/${idx}`
      );
      audioRef.current = el;
      el.onended = (): void => {
        clearRevealTimers();
        setShown(chapter.toBlock + 1); // guarantee the chapter is fully revealed
        const next = idx + 1;
        if (next < narration.chapters.length) {
          startChapter(next);
        } else {
          setPlaying(false);
          setCaption("");
        }
      };
      void el.play().catch(() => {
        // Autoplay can be blocked until a user gesture; the toggle/▶ click is a
        // gesture, so this path is unlikely. If it fails, stop cleanly.
        setPlaying(false);
      });
    },
    [narration, sessionId, clearRevealTimers]
  );

  // Silent (no-narration) auto-advance: one block per tick while playing.
  useEffect(() => {
    if (narrationActive) return undefined; // narration mode drives its own reveal
    if (!playing) return undefined;
    if (shown >= total) {
      setPlaying(false);
      return undefined;
    }
    const t = window.setTimeout(() => setShown((s) => Math.min(total, s + 1)), DWELL_MS);
    return () => window.clearTimeout(t);
  }, [playing, shown, total, narrationActive]);

  // Keep the newest revealed block in view while playing.
  useEffect(() => {
    if (playing && convRef.current !== null) {
      convRef.current.scrollTop = convRef.current.scrollHeight;
    }
  }, [shown, playing]);

  // Stop audio + timers on unmount.
  useEffect(() => stopAudio, [stopAudio]);

  const atEnd = shown >= total;

  /** ▶ / ⏸ / ↻ for both modes. Narration mode plays chapter-by-chapter. */
  const togglePlay = (): void => {
    if (narrationActive) {
      if (playing) {
        stopAudio();
        setPlaying(false);
        return;
      }
      // (Re)start: from the top if we were at the end, else resume the current
      // chapter from its start (audio cannot be mid-resumed deterministically).
      const startIdx = atEnd ? 0 : chapterIdx;
      setPlaying(true);
      startChapter(startIdx);
      return;
    }
    if (atEnd) {
      setShown(1);
      setPlaying(true);
    } else {
      setPlaying((p) => !p);
    }
  };

  /** Stop everything and seek to a block index (scrub / ⏮ / ⏭). */
  const seekTo = (n: number): void => {
    stopAudio();
    setPlaying(false);
    setCaption("");
    setShown(Math.min(total, Math.max(0, n)));
  };

  /** Toggle the 🔊 narration mode; turning it off falls back to silent reveal. */
  const toggleNarration = (): void => {
    stopAudio();
    setPlaying(false);
    setCaption("");
    setNarrationOn((on) => !on);
  };

  // Chapter ToC (章节目录): only when explicitly enabled AND a narration manifest
  // supplies enough chapters to be worth navigating. The narration chapters are
  // the natural chapter boundaries; we never synthesize chapters without one.
  const tocChapters = useMemo(
    () => (showToc && hasNarration && narration !== undefined ? narration.chapters : []),
    [showToc, hasNarration, narration]
  );
  const showTocRail = tocChapters.length >= TOC_MIN_CHAPTERS;

  // The active chapter is the one whose [fromBlock, toBlock] range contains the
  // latest revealed block (shown - 1). Recomputed from `shown`, so the highlight
  // follows reveal / playback automatically. -1 when nothing is revealed yet.
  const activeChapterIdx = useMemo(() => {
    if (!showTocRail) return -1;
    const latest = Math.max(0, shown - 1);
    return tocChapters.findIndex(
      (c) => latest >= c.fromBlock && latest <= c.toBlock
    );
  }, [showTocRail, tocChapters, shown]);

  /**
   * ToC click: jump to a chapter's first block. We seek so the chapter's first
   * block is REVEALED (shown = fromBlock + 1) — this both shows that block and
   * lands the active-chapter highlight (computed from shown - 1) inside the
   * clicked chapter. seekTo stops audio + pauses; we align `chapterIdx` so a
   * subsequent ▶ in narration mode resumes from this chapter. We never start
   * audio on a plain ToC click.
   */
  const selectChapter = (idx: number): void => {
    const chapter = tocChapters[idx];
    if (chapter === undefined) return;
    seekTo(chapter.fromBlock + 1);
    setChapterIdx(idx);
  };

  // Walk messages, revealing only blocks whose global index is < shown.
  let acc = 0;

  return (
    <div className="replay-view">
      <div className="replay-header">
        <div className="replay-title">{payload.title}</div>
        <div className="replay-meta">
          {payload.workspace !== undefined && payload.workspace.length > 0 && (
            <MetaChip label="工作区" value={payload.workspace} />
          )}
          {payload.adeRole !== undefined && payload.adeRole.length > 0 && (
            <MetaChip label="角色" value={payload.adeRole} />
          )}
          {payload.model !== undefined && payload.model.length > 0 && (
            <MetaChip label="模型" value={payload.model} />
          )}
        </div>
      </div>

      <div className={`replay-body${showTocRail ? " replay-body-with-toc" : ""}`}>
        {showTocRail && (
          // Chapter ToC rail: a scrollable, height-capped list of chapters; the
          // active chapter (tracking reveal) is highlighted + aria-current.
          <nav className="replay-toc" aria-label="章节目录">
            <ul className="replay-toc-list" role="list">
              {tocChapters.map((chapter, idx) => {
                const isActive = idx === activeChapterIdx;
                return (
                  <li key={`${chapter.fromBlock}-${chapter.toBlock}`} className="replay-toc-li">
                    <button
                      type="button"
                      className={`replay-toc-item${isActive ? " is-active" : ""}`}
                      onClick={() => selectChapter(idx)}
                      aria-current={isActive ? "true" : undefined}
                      title={chapter.text}
                    >
                      <span className="replay-toc-num">{idx + 1}.</span>
                      <span className="replay-toc-label">{truncateLabel(chapter.text)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}

        <div className="replay-conversation" ref={convRef}>
          {payload.messages.map((message, mi) => {
            const start = acc;
            acc += message.blocks.length;
            const visible = message.blocks.filter((_, bi) => start + bi < shown);
            if (visible.length === 0) return null;
            return (
              <div
                key={`${message.role}-${mi}`}
                className={`replay-message replay-message-${message.role}`}
              >
                <div className="replay-bubble">
                  {visible.map((block) => (
                    <BlockRenderer key={block.id} block={block} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {total > 1 && (
        // Player-style transport bar at the BOTTOM (like a normal video player).
        <div className="replay-playbar">
          <button
            type="button"
            className="replay-playbar-btn"
            onClick={() => seekTo(narrationActive ? 0 : 1)}
            title="回到开头"
            aria-label="回到开头"
          >
            ⏮
          </button>
          <button
            type="button"
            className="replay-playbar-btn"
            onClick={togglePlay}
            title={playing ? "暂停" : "播放"}
            aria-label={playing ? "暂停" : "播放"}
          >
            {playing ? "⏸" : atEnd ? "↻" : "▶"}
          </button>
          <button
            type="button"
            className="replay-playbar-btn"
            onClick={() => seekTo(shown + 1)}
            disabled={atEnd}
            title="下一步"
            aria-label="下一步"
          >
            ⏭
          </button>
          <input
            className="replay-scrubber"
            type="range"
            min={0}
            max={total}
            value={shown}
            onChange={(e) => seekTo(Number(e.target.value))}
            aria-label="回放进度"
          />
          <span className="replay-playbar-count">
            {shown} / {total}
          </span>
          {hasNarration && (
            <button
              type="button"
              className={`replay-playbar-btn replay-narration-toggle${
                narrationOn ? " is-on" : ""
              }`}
              onClick={toggleNarration}
              title={narrationOn ? "关闭旁白" : "开启旁白"}
              aria-label={narrationOn ? "关闭旁白" : "开启旁白"}
              aria-pressed={narrationOn}
            >
              {narrationOn ? "🔊" : "🔇"}
            </button>
          )}
          {loomLiveEnabled && connected === true && seedPrompt !== "" && (
            <button
              type="button"
              className={`replay-playbar-try${tryMode ? " is-on" : ""}`}
              onClick={() => setTryMode((v) => !v)}
              title="用你自己的织灵账号，把这个案例真跑一遍"
              aria-pressed={tryMode}
            >
              {tryMode ? "收起" : "在你的织灵里试跑"}
            </button>
          )}
        </div>
      )}

      {narrationActive && caption.length > 0 && (
        // 旁白字幕: the current chapter's spoken line, under the play-bar.
        <div className="replay-caption" role="status">
          {caption}
        </div>
      )}

      {tryMode && (
        // C→D: the live run of this case in the user's own 织灵, reusing the same
        // block views as the recording above — just growing in real time. Scroll it
        // into view on open, since the recording above can be long.
        <div
          className="replay-tryrun"
          ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "start" })}
        >
          <div className="replay-tryrun-head">在你的织灵里跑这一个</div>
          <LiveRunBlock prompt={seedPrompt} />
        </div>
      )}
    </div>
  );
}
